import React, { useEffect, useState } from "react";
import { useSiteContent } from "../../context/SiteContentContext";

const FALLBACK_VALUE = "—";

function normalizeRow(row, index) {
  return {
    id: `${row.raw_date ?? "date"}-${row.groom ?? "groom"}-${index}`,
    groom: row.groom ?? FALLBACK_VALUE,
    date: row.raw_date ?? FALLBACK_VALUE,
    notes: row.notes ?? FALLBACK_VALUE,
  };
}

function getSortValue(rawDate, year) {
  const parts = String(rawDate ?? "").match(/\d+/g)?.map(Number) ?? [];

  if (parts.length < 2) {
    return Number.MAX_SAFE_INTEGER;
  }

  const day = parts[0];
  const month = parts.length === 2 ? parts[1] : parts.at(-2);

  if (!Number.isFinite(day) || !Number.isFinite(month)) {
    return Number.MAX_SAFE_INTEGER;
  }

  return new Date(year, month - 1, day).getTime();
}

function normalizeYearGroup(group) {
  const year = Number(group.year);
  const rows = (group.weddings ?? [])
    .map((row, index) => normalizeRow(row, index))
    .sort((firstRow, secondRow) => {
      return getSortValue(firstRow.date, year) - getSortValue(secondRow.date, year);
    });

  return {
    year,
    rows,
  };
}

export default function WeddingsTable() {
  const [yearGroups, setYearGroups] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const { tables } = useSiteContent();

  useEffect(() => {
    let isMounted = true;

    async function loadWeddings() {
      try {
        const response = await fetch("/data/weddings.json");

        if (!response.ok) {
          throw new Error("Failed to load weddings data.");
        }

        const data = await response.json();

        if (!isMounted) {
          return;
        }

        const groups = (data.years ?? [])
          .filter((group) => Number.isFinite(Number(group.year)))
          .map((group) => normalizeYearGroup(group))
          .sort((firstGroup, secondGroup) => secondGroup.year - firstGroup.year);

        setYearGroups(groups);
        setSelectedYear((currentSelection) => currentSelection ?? groups[0]?.year ?? null);
      } catch (error) {
        console.error("تعذر تحميل أرشيف الإكليل:", error);
      }
    }

    loadWeddings();

    return () => {
      isMounted = false;
    };
  }, []);

  const activeGroup =
    yearGroups.find((group) => group.year === selectedYear) ?? yearGroups[0] ?? null;

  const showNotesColumn = activeGroup?.rows.some(
    (row) => row.notes !== FALLBACK_VALUE,
  );

  if (yearGroups.length === 0 || !activeGroup) {
    return (
      <div className="rounded-[24px] border border-[#eadfce] bg-[#fcfaf6] px-5 py-6 text-right text-[#5d5348]">
        {tables.emptyLabel}
      </div>
    );
  }

  return (
    <div className="space-y-6" dir="rtl">
      <section className="rounded-[28px] border border-[#eadfce] bg-[#fcfaf6] p-4 md:p-5">
        <div className="mb-4 flex flex-col gap-2 text-right">
          <h2 className="text-xl font-bold text-[#3e352b]">اختر السنة</h2>
          <p className="text-sm text-[#7b6a54]">
            اضغط على السنة لعرض قائمة الأكاليل الخاصة بها.
          </p>
        </div>

        <div className="overflow-x-auto pb-1">
          <div className="flex min-w-max gap-3">
            {yearGroups.map((group) => {
              const isActive = group.year === activeGroup.year;

              return (
                <button
                  key={group.year}
                  type="button"
                  onClick={() => setSelectedYear(group.year)}
                  aria-pressed={isActive}
                  className={`rounded-full border px-5 py-3 text-sm font-bold transition-colors ${
                    isActive
                      ? "border-[#8a7355] bg-[#8a7355] text-white shadow-[0_10px_30px_rgba(76,58,37,0.12)]"
                      : "border-[#dfd2bf] bg-white text-[#6d5d49] hover:border-[#bda688] hover:text-[#3e352b]"
                  }`}
                >
                  {group.year}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      <section className="overflow-hidden rounded-[28px] border border-[#eadfce] bg-[#fcfaf6]">
        <div className="flex flex-col gap-3 border-b border-[#eadfce] bg-[#f6efe4] px-5 py-4 text-right md:flex-row md:items-center md:justify-between">
          <div>
            <h3 className="text-2xl font-bold text-[#3e352b]">{activeGroup.year}</h3>
            <p className="mt-1 text-sm text-[#7b6a54]">
              {`عدد المواعيد: ${activeGroup.rows.length}`}
            </p>
          </div>

          <span className="inline-flex w-fit rounded-full bg-white px-4 py-2 text-sm font-bold text-[#8a7355] shadow-[0_10px_30px_rgba(76,58,37,0.08)]">
            {`أكاليل ${activeGroup.year}`}
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full border-separate border-spacing-0 text-right">
            <thead>
              <tr className="bg-[#fffdf9] text-[#6d5d49]">
                <th className="border-b border-[#eadfce] px-5 py-4 text-sm font-bold">
                  {tables.weddingsNameLabel}
                </th>
                <th className="border-b border-[#eadfce] px-5 py-4 text-sm font-bold">
                  {tables.dateLabel}
                </th>
                {showNotesColumn ? (
                  <th className="border-b border-[#eadfce] px-5 py-4 text-sm font-bold">
                    {tables.notesLabel}
                  </th>
                ) : null}
              </tr>
            </thead>

            <tbody className="text-[#4b4034]">
              {activeGroup.rows.map((row, index) => (
                <tr
                  key={row.id}
                  className={index % 2 === 0 ? "bg-white" : "bg-[#f8f4ee]"}
                >
                  <td className="border-b border-[#f0e6d8] px-5 py-4 align-top text-sm leading-7">
                    {row.groom}
                  </td>
                  <td className="border-b border-[#f0e6d8] px-5 py-4 align-top text-sm leading-7 text-[#6d5d49]">
                    {row.date}
                  </td>
                  {showNotesColumn ? (
                    <td className="border-b border-[#f0e6d8] px-5 py-4 align-top text-sm leading-7 text-[#6d5d49]">
                      {row.notes}
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
